import { ApolloClient } from '@apollo/client';
import { GetHelloSignUrlQuery } from 'sellDirect/graphql/useHelloSignUrl';
import {
  GetHelloSignUrl,
  GetHelloSignUrlVariables,
} from 'sellDirect/graphql/__generated__/GetHelloSignUrl';
import { isFetchResponseReauthRequired } from '@opendoor/auth-fe';
import { useObservability } from 'shared/helpers/observability';
import type { Observability } from 'shared/helpers/observability';
import { HelloSignTaskFields_signers } from 'sellDirect/helpers/__generated__/HelloSignTaskFields';
import { WEB_API_URL } from 'shared/helpers/globals';
import { useReauth } from 'platform/components/ReauthContext';
import { useCallback } from 'react';
import {
  getGraphqlErrorType,
  HelloSignUnexpectedError,
  HelloSignError,
  HelloSignReauthRequiredError,
  showToastForHelloSignError,
} from 'sellDirect/helpers/closingDash/errorHandler';

/** Returns signing URL that should be plugged in to the HelloSign SDK. */
export const getHelloSignUrl = async (
  apolloClient: ApolloClient<unknown>,
  observability: Observability,
  signer: HelloSignTaskFields_signers,
): Promise<string | HelloSignError> => {
  const result = await apolloClient.query<GetHelloSignUrl, GetHelloSignUrlVariables>({
    query: GetHelloSignUrlQuery,
    variables: {
      signatureId: signer.id,
    },
    context: {
      linkContext: {
        team: 'sell-direct',
      },
    },
  });

  const errorType = getGraphqlErrorType(result, observability, 'hellosign_url_query');
  if (errorType) {
    return errorType;
  }

  const url = result.data.seller.helloSignUrl;
  if (!url) {
    observability.trackError('hellosign_url_query-error_empty');
    return HelloSignUnexpectedError;
  }

  return url;
};

/**
 * Triggers a download of a HelloSign document.
 * If reauthentication is required, the reauth will be triggered first.
 */
export const useHelloSignDownload = () => {
  const observability = useObservability();
  const { showReauth } = useReauth();
  const resultFn = useCallback(
    async (helloSignTaskId: string) => {
      const url = await getHelloSignDownloadUrl(observability, helloSignTaskId);
      if (typeof url === 'string') {
        window.open(url, '_blank'); // open URL in a new tab
      } else if (url === HelloSignReauthRequiredError) {
        showReauth({
          onSuccess: () => resultFn(helloSignTaskId), // call onClickSign again, which should work the second time around
          onCancel: () => {},
        });
      } else {
        showToastForHelloSignError(url);
      }
    },
    [observability, showReauth],
  );
  return resultFn;
};

/** Returns HelloSign download URL of a HelloSign document. */
const getHelloSignDownloadUrl = async (
  observability: Observability,
  helloSignTaskId: string,
): Promise<string | HelloSignError> => {
  const resp = await fetch(`${WEB_API_URL}/api/v1/contracts/${helloSignTaskId}/preview`, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });
  if (resp.status === 200) {
    try {
      const json = await resp.json();
      const fileUrl = json['file_url'];
      if (fileUrl) {
        return fileUrl;
      }
      observability.trackError('hellosign_preview-error_missing_file_url');
      return HelloSignUnexpectedError;
    } catch (e: CatchError) {
      observability.trackError('hellosign_preview-error_response', e);
      return HelloSignUnexpectedError;
    }
  }

  const isReauthRequired = await isFetchResponseReauthRequired(resp);
  if (isReauthRequired) {
    return HelloSignReauthRequiredError;
  }

  observability.trackError('hellosign_preview-error');
  return HelloSignUnexpectedError;
};
